/**
 * Mensagens (migrations 250/251): tipos e regras puras da tela — sem banco,
 * para testar sem montar nada.
 */

export type TipoConversa = 'operacao' | 'equipe' | 'direta' | 'grupo';

export type Conversa = {
  id: string;
  tipo: TipoConversa;
  titulo: string;
  /** A outra pessoa da conversa direta, ou o profissional no chat da operação (visto pela operação). */
  outroId: string | null;
  ultimaEm: string | null;
  ultimaTexto: string | null;
  ultimaAutorNome: string | null;
  ultimaMinha: boolean;
  naoLidas: number;
};

export type Anexo = { caminho: string; nome: string; tipo: string; tamanho: number };

export type Mensagem = {
  id: string;
  conversaId: string;
  autorId: string;
  autorNome: string;
  texto: string;
  anexos: Anexo[];
  createdAt: string;
  apagadaEm: string | null;
};

export type Pessoa = { userId: string; nome: string; papel: string };

export const LIMITE_ARQUIVO = 25 * 1024 * 1024;
export const MAX_ARQUIVOS = 10;
export const PAGINA = 50;

const txt = (v: unknown) => (v == null ? '' : String(v));

export const normalizarConversa = (r: Record<string, unknown>): Conversa => ({
  id: txt(r.id),
  tipo: (txt(r.tipo) || 'direta') as TipoConversa,
  titulo: txt(r.titulo).trim() || 'Conversa',
  outroId: r.outro_id ? txt(r.outro_id) : null,
  ultimaEm: r.ultima_em ? txt(r.ultima_em) : null,
  ultimaTexto: r.ultima_texto ? txt(r.ultima_texto) : null,
  ultimaAutorNome: r.ultima_autor_nome ? txt(r.ultima_autor_nome) : null,
  ultimaMinha: r.ultima_minha === true,
  naoLidas: Number(r.nao_lidas) || 0,
});

const normalizarAnexos = (v: unknown): Anexo[] =>
  Array.isArray(v)
    ? v
        .filter((a) => a && typeof a === 'object' && (a as Anexo).caminho)
        .map((a) => ({
          caminho: txt((a as Anexo).caminho),
          nome: txt((a as Anexo).nome) || 'arquivo',
          tipo: txt((a as Anexo).tipo) || 'application/octet-stream',
          tamanho: Number((a as Anexo).tamanho) || 0,
        }))
    : [];

export const normalizarMensagem = (r: Record<string, unknown>): Mensagem => ({
  id: txt(r.id),
  conversaId: txt(r.conversa_id),
  autorId: txt(r.autor_id),
  autorNome: txt(r.autor_nome) || 'Usuário',
  texto: txt(r.texto),
  anexos: normalizarAnexos(r.anexos),
  createdAt: txt(r.created_at),
  apagadaEm: r.apagada_em ? txt(r.apagada_em) : null,
});

/** O meu chat com a operação fica sempre no topo; o resto, do mais recente para o mais antigo. */
export const ordenarConversas = (lista: Conversa[]): Conversa[] =>
  [...lista].sort((a, b) => {
    const fixaA = a.tipo === 'operacao' && !a.outroId ? 1 : 0;
    const fixaB = b.tipo === 'operacao' && !b.outroId ? 1 : 0;
    if (fixaA !== fixaB) return fixaB - fixaA;
    return (b.ultimaEm ?? '').localeCompare(a.ultimaEm ?? '');
  });

export const totalNaoLidas = (lista: Conversa[]) => lista.reduce((s, c) => s + c.naoLidas, 0);

export const semAcento = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

export const filtrarPorNome = <T>(lista: T[], busca: string, nome: (x: T) => string): T[] => {
  const b = semAcento(busca.trim());
  return b ? lista.filter((x) => semAcento(nome(x)).includes(b)) : lista;
};

/** Junta mensagens novas às que já estão na tela (sem repetir) em ordem de envio. */
export const juntarMensagens = (atuais: Mensagem[], novas: Mensagem[]): Mensagem[] => {
  const porId = new Map(atuais.map((m) => [m.id, m]));
  for (const m of novas) porId.set(m.id, m);
  return [...porId.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
};

const diaLocal = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const DIA_MS = 86400000;

export const rotuloDia = (iso: string, agora = new Date()): string => {
  const d = new Date(iso);
  const dif = Math.round((diaLocal(agora) - diaLocal(d)) / DIA_MS);
  if (dif === 0) return 'Hoje';
  if (dif === 1) return 'Ontem';
  return d.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    ...(d.getFullYear() !== agora.getFullYear() ? { year: 'numeric' } : {}),
  });
};

/** Mensagens separadas por dia, para o "Hoje / Ontem / 12 de outubro" entre elas. */
export const agruparPorDia = (msgs: Mensagem[], agora = new Date()) => {
  const grupos: { dia: string; mensagens: Mensagem[] }[] = [];
  for (const m of msgs) {
    const dia = rotuloDia(m.createdAt, agora);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo?.dia === dia) ultimo.mensagens.push(m);
    else grupos.push({ dia, mensagens: [m] });
  }
  return grupos;
};

export const horaCurta = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

/** Hora na lista de conversas: hoje → 14:32; ontem → Ontem; na semana → seg; antes → 03/10. */
export const quandoNaLista = (iso: string | null, agora = new Date()): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const dif = Math.round((diaLocal(agora) - diaLocal(d)) / DIA_MS);
  if (dif === 0) return horaCurta(iso);
  if (dif === 1) return 'Ontem';
  if (dif < 7) return d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

/** "Você: ok" / "Ana: bom dia" / "bom dia" (direta, quando é o outro). */
export const previa = (c: Conversa): string => {
  if (!c.ultimaTexto) {
    return 'Nenhuma mensagem';
  }
  if (c.ultimaMinha) return `Você: ${c.ultimaTexto}`;
  if (c.tipo === 'direta' || !c.ultimaAutorNome) return c.ultimaTexto;
  return `${c.ultimaAutorNome.split(' ')[0]}: ${c.ultimaTexto}`;
};

export const iniciais = (nome: string) =>
  nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('') || '?';

export const ehImagem = (a: Pick<Anexo, 'tipo'>) => a.tipo.startsWith('image/');

export const tamanhoLegivel = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
};

/** Barra arquivo grande demais ou arquivos demais antes de subir qualquer coisa. */
export const problemaNosArquivos = (arquivos: Pick<File, 'name' | 'size'>[]): string | null => {
  if (arquivos.length > MAX_ARQUIVOS) return `No máximo ${MAX_ARQUIVOS} arquivos por mensagem.`;
  const grande = arquivos.find((f) => f.size > LIMITE_ARQUIVO);
  return grande ? `${grande.name} excede o limite de 25 MB.` : null;
};

export const descricaoDoTipo = (c: Pick<Conversa, 'tipo' | 'outroId'>): string => {
  switch (c.tipo) {
    case 'operacao':
      return 'Atendimento da operação';
    case 'equipe':
      return 'Grupo da equipe';
    case 'grupo':
      return 'Grupo';
    default:
      return 'Conversa direta';
  }
};

/** Erro de banco sem as migrations 250/251 → aviso legível. */
export const mensagemDeErro = (e: unknown, padrao: string): string => {
  const msg = e instanceof Error ? e.message : '';
  if (/schema cache|does not exist|42P01|42883|PGRST20[25]/i.test(msg)) {
    return 'Mensagens indisponíveis no momento.';
  }
  return msg || padrao;
};
